import { Box, List, ListSubheader, styled } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import useBaseLinks from 'store/Auth/Links/useBaseLinks';
import sidebarMenuItems, { MenuItem, MenuSection } from './SidebarMenuItems';
import SidebarMenuItem from './item';
import { useSelector } from 'store';
import useAuth from 'store/Auth/useAuth';

const itemsToByPassCheckOn = ['Overview', 'Statistics'];

const MenuWrapper = styled(Box)(
  () => `
  width: 100%;
`,
);

const renderSidebarMenuItems = ({
  items,
  path,
  isExpanded,
}: {
  items: MenuItem[];
  path: string;
  handleLogout?: () => void;
  isExpanded: boolean;
}): JSX.Element => (
  <List
    component="div"
    style={{
      width: '100%',
      display: 'flex',
      alignItems: isExpanded ? 'flex-start' : 'center',
      flexDirection: 'column',
      gap: '16px',
    }}
  >
    {items.reduce(
      (ev: JSX.Element[], item) =>
        reduceChildRoutes({ ev, item, path, isExpanded }),
      [],
    )}
  </List>
);

const reduceChildRoutes = ({
  ev,
  path,
  item,
  isExpanded,
}: {
  ev: JSX.Element[];
  path: string;
  item: MenuItem;
  isExpanded: boolean;
}): Array<JSX.Element> => {
  const key = item.name;
  const exactMatch = item.link
    ? !!matchPath(
        {
          path: item.link,
          end: true,
        },
        path,
      )
    : false;

  if (item.items && item.items.length) {
    const partialMatch = item.link
      ? !!matchPath(
          {
            path: item.link,
            end: false,
          },
          path,
        )
      : false;

    ev.push(
      <SidebarMenuItem
        key={key}
        active={exactMatch}
        open={partialMatch}
        name={item.name}
        icon={item.icon}
        link={item.link}
        badge={item.badge}
        isExpanded={isExpanded}
        onClick={item.onClick}
      >
        {renderSidebarMenuItems({
          path,
          items: item.items,
          isExpanded: true,
        })}
      </SidebarMenuItem>,
    );
  } else {
    ev.push(
      <SidebarMenuItem
        key={key}
        active={exactMatch}
        name={item.name}
        link={item.link}
        badge={item.badge}
        icon={item.icon}
        isExpanded={isExpanded}
        className={item.className}
        onClick={item.onClick}
      />,
    );
  }

  return ev;
};

type SideBarMenuProps = {
  isExpanded: boolean;
};

const SidebarMenu: FC<SideBarMenuProps> = ({ isExpanded }): JSX.Element => {
  const location = useLocation();
  const { profile } = useAuth();
  const [filteredMenu, setFilteredMenu] = useState<MenuSection[]>([]);
  const { getLink } = useBaseLinks();
  const { map } = useSelector((state) => state.baseLinks);

  useEffect(() => {
    const filterSection = (section: MenuSection): MenuSection | null => {
      const canShowItem = (item: MenuItem) => {
        const { id, name } = item;
        //Kept default for now | Will remove in future
        if (
          //If user is customer then statistics should be hidden
          profile?.role === 'customer'
        ) {
          const itemIndex = itemsToByPassCheckOn.indexOf('Statistics');
          if (itemIndex > -1) {
            itemsToByPassCheckOn.splice(itemIndex, 1);
          }
        }
        if (itemsToByPassCheckOn.includes(name)) {
          return true;
        }

        return !!getLink(id);
      };

      // Recursive function to filter items and sub-items
      const filterItems = (items: MenuItem[]): MenuItem[] => {
        return items
          .filter((item) => canShowItem(item))
          .map((item) => {
            const link = getLink(item.id);
            if (
              item.id === 'auto-recordings' ||
              item.id === 'manual-recordings'
            ) {
              item.link = link ?? undefined;
            }
            if (item.items && item.items.length > 0) {
              return {
                ...item,
                items: filterItems(item.items),
              };
            }
            return item;
          });
      };

      // Filter the items in the section (including nested items)
      const filteredItems = filterItems(section.items);

      if (filteredItems.length > 0) {
        return {
          heading: section.heading,
          items: filteredItems,
        };
      }

      return null;
    };

    const getFilteredMenu = () => {
      const thisFilteredMenu: MenuSection[] = [];
      let i = 0;
      while (i < sidebarMenuItems.length) {
        const section = sidebarMenuItems[i];
        const filteredSection = filterSection(section);
        if (filteredSection) {
          thisFilteredMenu.push(filteredSection);
        }
        i++;
      }

      return thisFilteredMenu;
    };
    if (map) {
      setFilteredMenu(getFilteredMenu());
    }
  }, [map, getLink, profile?.role]);

  return (
    <>
      {filteredMenu.map(
        (section, index) =>
          section && (
            <MenuWrapper key={index}>
              <List
                component="div"
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: isExpanded ? 'flex-start' : 'center',
                  flexDirection: 'column',
                }}
                subheader={
                  <ListSubheader
                    component="div"
                    style={{
                      color: '#001520',
                      fontSize: isExpanded ? 14 : 12,
                      fontWeight: 600,
                    }}
                    disableSticky
                  >
                    {section.heading}
                  </ListSubheader>
                }
              >
                {renderSidebarMenuItems({
                  items: section.items,
                  path: location.pathname,
                  isExpanded: isExpanded,
                })}
              </List>
            </MenuWrapper>
          ),
      )}
    </>
  );
};

export default SidebarMenu;
